// Документация:
// - Спека: https://www.saremba.de/chessgml/standards/pgn/pgn-complete.htm
// - Расширения: https://rpb-chessboard.yo35.org/documentation/pgn-syntax/
//
// TODO: из расширений
// - Null moves
// - [%ctl]

type FileName = 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h';
type RankName = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8';
type SquareName = `${FileName}${RankName}`;

/*
    ____,-------------------------------,____
    \   |           Интерфейсы          |   /
    /___|-------------------------------|___\
*/

export interface IMoveDto {
  color?: 'white' | 'black';
  moveFull?: number;
  san: string;
  alts?: IPgnVariantDto<IMoveDto>[];
  nags?: number[];
  comment?: string;

  // Embedded comands:

  // [%emt 1:30:51]
  timeElapsed?: number;

  // [%clk 1:30:51]
  timeRemaining?: number;

  // [%eval -0.28], [%eval #-5] (мат в 5 ходов), [%eval 1.28,56] (глубина 56)
  score?: IEvaluationScore;

  // [%csl Rd4,Gd5]
  coloredSquares?: IColoredSquare[];

  // [%cal Rc8f5,Ra8d8,Re8c8]
  coloredArrows?: IColoredArrow[];

  // Custom embedded commands

  // [%points 3] — points given for this move in tests
  points?: number;
}

export interface IPgnVariantMeta {
  prelude?: string;
  coloredSquares?: IColoredSquare[];
  coloredArrows?: IColoredArrow[];
}

export interface IPgnVariantDto<T extends IMoveDto = IMoveDto> extends IPgnVariantMeta {
  moves: T[];
}

export interface IPgnMove extends IMoveDto {
  id: string;
  altId?: string;
  color: 'white' | 'black';
  moveFull: number;
  alts?: PgnVariant[];
}

export interface IColoredSquare {
  color: 'R' | 'G' | 'B' | 'Y';
  square: SquareName;
}

export interface IColoredArrow {
  color: 'R' | 'G' | 'B' | 'Y';
  from: SquareName;
  to: SquareName;
}

// Лесенка очков из тега TestScoreLadder: максимум звезд и пороги нижних
// ступеней («столько-то звезд, если решение длиннее порога»)
export interface IScoreLadder {
  maxStars: number;
  thresholds: Partial<Record<number, number>>;
}

// Фишка головоломки из тега Tokens. type — открытая строка: неизвестные типы
// сохраняются при перезаписи, к известным сужает уже маппинг на доску
export interface IToken {
  type: string;
  square: SquareName;
}

export interface IEvaluationScore {
  type: 'cp' | 'mate';
  value: number;
  depth?: number;
}

/*
    ____,-------------------------------,____
    \   |  PgnVariant (aka Alternative) |   /
    /___|-------------------------------|___\
*/

export class PgnVariant<T extends IMoveDto = IPgnMove> implements IPgnVariantDto<T> {
  id: string;
  parentMoveId: string;
  prelude?: string;
  coloredSquares?: IColoredSquare[];
  coloredArrows?: IColoredArrow[];
  moves: T[];

  constructor({
    id,
    parentMoveId,
    prelude,
    coloredSquares,
    coloredArrows,
    moves,
  }: {
    id: string;
    parentMoveId: string;
    prelude?: string;
    coloredSquares?: IColoredSquare[];
    coloredArrows?: IColoredArrow[];
    moves: T[];
  }) {
    this.id = id;
    this.parentMoveId = parentMoveId;
    this.prelude = prelude;
    this.coloredSquares = coloredSquares;
    this.coloredArrows = coloredArrows;
    this.moves = moves;
  }

  serialize(startFullMove?: number): string {
    return serializeVariant(this, startFullMove);
  }
}

/*
    ____,-------------------------------,____
    \   |              PGN              |   /
    /___|-------------------------------|___\
*/

export class Pgn<T extends IMoveDto> implements IPgnVariantDto<T> {
  constructor({
    tags,
    moves,
    prelude,
    coloredSquares,
    coloredArrows,
  }: {
    tags?: Record<string, string>;
    moves?: T[];
    prelude?: string;
    coloredSquares?: IColoredSquare[];
    coloredArrows?: IColoredArrow[];
  } = {}) {
    this.moves = moves ?? [];
    this.prelude = prelude;
    this.coloredSquares = coloredSquares;
    this.coloredArrows = coloredArrows;
    if (tags) {
      this.event = this.#asStringNotQuestion(tags.Event);
      this.site = this.#asStringNotQuestion(tags.Site);
      this.date = this.#asDateStringNotQuestion(tags.Date);
      this.round = this.#asStringNotQuestion(tags.Round);
      this.white = this.#asStringNotQuestion(tags.White);
      this.black = this.#asStringNotQuestion(tags.Black);
      if (tags.Result && tags.Result !== '*' && ['1-0', '0-1', '1/2-1/2'].includes(tags.Result)) {
        this.result = tags.Result as Pgn<T>['result'];
      }
      this.fen = tags.FEN;
      this.whiteTitle = this.#asStringNotDash(tags.WhiteTitle);
      this.blackTitle = this.#asStringNotDash(tags.BlackTitle);
      this.whiteElo = this.#asInteger(tags.WhiteElo);
      this.blackElo = this.#asInteger(tags.BlackElo);
      this.annotator = tags.Annotator;
      this.eventDate = this.#asDateStringNotQuestion(tags.EventDate);
      this.section = tags.Section;
      this.stage = tags.Stage;
      this.board = this.#asInteger(tags.Board);
      this.timeControl = this.#asStringNotQuestion(tags.TimeControl);
      this.variant = tags.Variant;
      this.mode = tags.Mode;
      this.link = tags.Link;
      this.studyName = tags.StudyName;
      this.chapterName = tags.ChapterName;
      this.parentChapter = tags.ParentChapter;
      this.chapterType = tags.ChapterType;
      this.testScoringMode = tags.TestScoringMode;
      this.chapterLevel = tags.ChapterLevel;
      this.videoUrl = tags.VideoUrl;
      this.imageUrl = tags.ImageUrl;
      this.precedingMove = tags.PrecedingMove;
      this.whiteTimeControl = this.#asStringNotQuestion(tags.WhiteTimeControl);
      this.blackTimeControl = this.#asStringNotQuestion(tags.BlackTimeControl);
      this.seancerTimeControl = this.#asStringNotQuestion(tags.SeancerTimeControl);
      this.seancerColor =
        tags.SeancerColor && tags.SeancerColor !== '?'
          ? tags.SeancerColor.toLowerCase() === 'black'
            ? 'black'
            : 'white'
          : undefined;
      this.orientation = tags.Orientation
        ? tags.Orientation.toLowerCase() === 'black'
          ? 'black'
          : 'white'
        : undefined;
      this.topics = tags.Topics?.split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      this.tokens = parseTokens(tags.Tokens);
      this.scoreLadder = parseScoreLadder(tags.TestScoreLadder);
    }
  }

  // Tag Normalizers

  #asInteger(v?: string): number | undefined {
    if (v && /^\d+$/.test(v)) return parseInt(v, 10);
  }

  #asStringNotQuestion(v?: string) {
    if (v !== '?') return v;
  }

  #asStringNotDash(v?: string) {
    if (v !== '-') return v;
  }

  #asDateStringNotQuestion(v?: string) {
    if (v !== '????.??.??') return v;
  }

  // Prelude Metadata

  prelude?: string;
  coloredSquares?: IColoredSquare[];
  coloredArrows?: IColoredArrow[];

  // The "Seven Tag Roster"

  event?: string;
  site?: string;
  date?: string;
  round?: string;
  white?: string;
  black?: string;
  result?: '1-0' | '0-1' | '1/2-1/2';

  // Other Tags

  fen?: string;
  whiteTitle?: string;
  blackTitle?: string;
  whiteElo?: number;
  blackElo?: number;
  annotator?: string;
  eventDate?: string;
  section?: string;
  stage?: string;
  board?: number;
  timeControl?: string | '-'; /* "-" — игра без контроля времени */
  variant?: string;
  mode?: string;
  link?: string;
  studyName?: string;
  chapterName?: string;
  // Собственное имя родительской главы (вложенность глав в книге студии)
  parentChapter?: string;
  chapterType?: string;
  testScoringMode?: string;
  chapterLevel?: string;
  videoUrl?: string;
  imageUrl?: string;
  precedingMove?: string;
  whiteTimeControl?: string;
  blackTimeControl?: string;
  seancerTimeControl?: string;
  seancerColor?: 'white' | 'black';
  orientation?: 'white' | 'black';
  topics?: string[];
  // Фишки головоломки (звездочки, стены, ...) — тег Tokens, см. parseTokens
  tokens?: IToken[];
  // Лесенка очков теста — тег TestScoreLadder, см. parseScoreLadder
  scoreLadder?: IScoreLadder;

  // Moves

  moves: T[];

  // Tags serialization

  walkTags(addTag: (tag?: string, value?: string | number) => void) {
    // seven tag roster
    addTag('Event', this.event ?? '?');
    addTag('Site', this.site ?? '?');
    addTag('Date', this.date ?? '????.??.??');
    addTag('Round', this.round ?? '?');
    addTag('White', this.white ?? '?');
    addTag('Black', this.black ?? '?');
    addTag('Result', this.result ?? '*');
    if (this.fen) {
      addTag('SetUp', '1');
      addTag('FEN', this.fen);
    }
    addTag('Tokens', serializeTokens(this.tokens));
    addTag('WhiteTitle', this.whiteTitle);
    addTag('BlackTitle', this.blackTitle);
    addTag('WhiteElo', this.whiteElo);
    addTag('BlackElo', this.blackElo);
    addTag('Annotator', this.annotator);
    addTag('EventDate', this.eventDate);
    addTag('Section', this.section);
    addTag('Stage', this.stage);
    addTag('Board', this.board);
    addTag('TimeControl', this.timeControl);
    addTag('Variant', this.variant);
    addTag('Mode', this.mode);
    addTag('Link', this.link);
    addTag('StudyName', this.studyName);
    addTag('ChapterName', this.chapterName);
    addTag('ParentChapter', this.parentChapter);
    addTag('ChapterType', this.chapterType);
    addTag('TestScoringMode', this.testScoringMode);
    addTag('TestScoreLadder', serializeScoreLadder(this.scoreLadder));
    addTag('ChapterLevel', this.chapterLevel);
    addTag('VideoUrl', this.videoUrl);
    addTag('ImageUrl', this.imageUrl);
    addTag('PrecedingMove', this.precedingMove);
    addTag('WhiteTimeControl', this.whiteTimeControl);
    addTag('BlackTimeControl', this.blackTimeControl);
    addTag('SeancerTimeControl', this.seancerTimeControl);
    if (this.seancerColor) {
      addTag('SeancerColor', this.seancerColor === 'black' ? 'Black' : 'White');
    }
    if (this.orientation) {
      addTag('Orientation', this.orientation === 'black' ? 'Black' : 'White');
    }
    if (this.topics?.length) {
      addTag('Topics', this.topics.filter(Boolean).join(', '));
    }
  }

  // Instance methods

  serialize() {
    const result = [];

    // Headers

    this.walkTags((tag?: string, value?: string | number) => {
      if (tag && value !== undefined) {
        result.push(`[${tag} "${escapeTagValue(value.toString())}"]`);
      }
    });

    // Moves

    result.push('');
    const body = serializeVariant(this);
    const winner = this.result ?? '*';
    result.push(body ? `${body} ${winner}` : winner);

    // Result

    return result.join('\n');
  }

  // Immutable copy с обновлениями

  copy(
    updates: {
      rawTags?: Record<string, string>;
      moves?: T[];
      prelude?: string;
      coloredSquares?: IColoredSquare[];
      coloredArrows?: IColoredArrow[];
    } = {},
  ): Pgn<T> {
    if (updates.rawTags) {
      return new Pgn<T>({
        tags: updates.rawTags,
        moves: 'moves' in updates ? updates.moves : this.moves,
        prelude: 'prelude' in updates ? updates.prelude : this.prelude,
        coloredArrows: 'coloredArrows' in updates ? updates.coloredArrows : this.coloredArrows,
        coloredSquares: 'coloredSquares' in updates ? updates.coloredSquares : this.coloredSquares,
      });
    }
    // HACK: избегаем повторного парсинга тэгов
    return Object.assign(new Pgn<T>({}), this, updates);
  }

  copyTags() {
    const result: Record<string, string> = {};

    this.walkTags((tag?: string, value?: string | number) => {
      if (tag && value !== undefined) {
        result[tag] = value.toString();
      }
    });

    return result;
  }

  // Static methods

  static parse(pgn: string): Pgn<IPgnMove> | undefined {
    return this.#parse(pgn, true)[0];
  }

  static parseMany(pgn: string): Pgn<IPgnMove>[] {
    return this.#parse(pgn);
  }

  // Helpers

  static #parse(pgn: string, single = false): Pgn<IPgnMove>[] {
    const split = pgn
      .replace(/\r/g, '')
      .split('\n\n')
      .filter((l) => l && !l.startsWith('%'));

    const results: Pgn<IPgnMove>[] = [];

    for (let i = 0; i < split.length; i += 1) {
      const h = split[i]?.trim();

      if (!h || !h.startsWith('[')) {
        continue;
      }

      // Собираем movetext
      let m = '';
      while (i + 1 < split.length && !split[i + 1]!.trimStart().startsWith('[')) {
        i += 1;
        m += (m ? '\n\n' : '') + split[i]!;
      }
      m = m.trim();

      if (!m) continue;

      const tags = parsePgnTags(h);
      const parsed = PgnMovetextParser.parse(m);
      results.push(
        new Pgn<IPgnMove>({
          tags,
          moves: parsed?.moves,
          prelude: parsed?.variantMeta?.prelude,
          coloredSquares: parsed?.variantMeta?.coloredSquares,
          coloredArrows: parsed?.variantMeta?.coloredArrows,
        }),
      );

      if (single) break;
    }

    return results;
  }
}

/*
    ____,-------------------------------,____
    \   |             Парсеры           |   /
    /___|-------------------------------|___\
*/

function parsePgnTags(source: string): Record<string, string> {
  const result: Record<string, string> = {};

  source.split('\n').forEach((l) => {
    const matches = /\[([\w\d]+)\s+"((?:\\.|[^"\\])*)"\s*\]/.exec(l);

    if (!matches) return;

    const [, name, value] = matches;

    if (!name || !value) return;

    result[name] = unescapeTagValue(value);
  });

  return result;
}

interface IParseResult {
  variantMeta?: IPgnVariantMeta;
  moves: IPgnMove[];
}

class PgnMovetextParser {
  readonly source: string;
  #at = 0;
  #lastFullMove = 0;
  #lastId = 0;
  #currentPrelude: IPgnVariantMeta | undefined;
  #currentAltId: string | undefined;

  private constructor(source: string) {
    this.source = source.replace(/\*|1-0|0-1|1\/2-1\/2$/g, '');
  }

  static parse(source: string): IParseResult | undefined {
    return new PgnMovetextParser(source).#parse();
  }

  #parse(): IParseResult | undefined {
    // save prev context
    const before = this.#currentPrelude;
    this.#currentPrelude = undefined;

    // parse

    const moves: IPgnMove[] = [];

    while (true) {
      const cur = this.#parseMove();
      if (!cur) break;

      if (cur.san === '--' && moves[moves.length - 1]?.san === '--') {
        throw new Error('PgnMovetextParser: consecutive null moves are not allowed');
      }

      moves.push(cur);
    }

    // restore prev context
    const variantMeta = this.#currentPrelude;
    this.#currentPrelude = before;

    if (!moves.length && !variantMeta) return undefined;

    return { variantMeta: variantMeta, moves };
  }

  #parseMove(): IPgnMove | undefined {
    // создаём заранее для потенциальных веток в ходе
    const moveId = `pgn-${this.#lastId++}`;
    let san: string | undefined;
    let moveFull: number | undefined;
    let color: 'white' | 'black' | undefined;
    let comment: string | undefined;
    let timeRemaining: number | undefined;
    let timeElapsed: number | undefined;
    let coloredSquares: IColoredSquare[] | undefined;
    let coloredArrows: IColoredArrow[] | undefined;
    let score: IEvaluationScore | undefined;
    let alts: PgnVariant[] | undefined;
    let nags: number[] | undefined;

    // Non-standard commands

    // points given for this move in tests
    let points: number | undefined;

    let preludeText: string | undefined;
    let preludeSquares: IColoredSquare[] | undefined;
    let preludeArrows: IColoredArrow[] | undefined;

    const commitPrelude = () => {
      if (preludeText || preludeSquares || preludeArrows) {
        this.#currentPrelude = {
          prelude: preludeText,
          coloredSquares: preludeSquares,
          coloredArrows: preludeArrows,
        };
      }
    };

    const addNag = (n: number) => {
      if (!nags) nags = [];
      nags.push(n);
    };

    while (this.#at < this.source.length) {
      const ch = this.source[this.#at]!;

      // Comments
      if (ch === '{') {
        const c = this.#parseComment();

        if (!san) {
          // prelude before first san
          if (c?.comment) {
            preludeText = preludeText ? preludeText + ' ' + c.comment : c.comment;
          }
          const commands = c?.commands;
          if (commands) {
            if (commands.csl) preludeSquares = mapClsSquares(commands.csl);
            if (commands.cal) preludeArrows = mapCalSquares(commands.cal);
          }
        } else {
          if (c?.comment) {
            comment = comment ? comment + ' ' + c.comment : c.comment;
          }

          const commands = c?.commands;
          if (commands) {
            if (commands.clk?.length === 1) {
              timeRemaining = timeToSeconds(commands.clk[0]!);
            }
            if (commands.emt?.length === 1) {
              timeElapsed = timeToSeconds(commands.emt[0]!);
            }
            if (commands.csl) {
              coloredSquares = mapClsSquares(commands.csl);
            }
            if (commands.cal) {
              coloredArrows = mapCalSquares(commands.cal);
            }
            if (commands.eval) {
              score = mapEval(commands.eval);
            }
            if (commands.points) {
              points = mapInteger(commands.points[0]!);
            }
          }
        }
      }
      // Alternatives
      else if (ch === '(') {
        this.#at++;
        if (san) {
          if (!alts) alts = [];

          const altId = `pgn-${this.#lastId++}`;
          const prevAltId = this.#currentAltId;
          const prevFullMove = this.#lastFullMove;
          this.#currentAltId = altId;
          const parsedAlt = this.#parse();
          this.#currentAltId = prevAltId;
          this.#lastFullMove = prevFullMove;

          if (parsedAlt) {
            const alt = new PgnVariant({
              id: altId,
              parentMoveId: moveId,
              prelude: parsedAlt.variantMeta?.prelude,
              coloredSquares: parsedAlt.variantMeta?.coloredSquares,
              coloredArrows: parsedAlt.variantMeta?.coloredArrows,
              moves: parsedAlt.moves,
            });
            alts.push(alt);
            // нормализация (flatten):
            // записи "(3. a3) (3. b3) (3. b4)" и "(3. a3 (3. b3 (3. b4)))" —
            // равнозначны.
            const firstAltMove = alt.moves[0];
            if (firstAltMove?.alts) {
              const flattened = firstAltMove.alts;
              flattened.forEach((l) => {
                l.parentMoveId = moveId;
              });
              alts.push(...flattened);
              firstAltMove.alts = undefined;
            }
          }
        }
      }
      // End of alternatives
      else if (ch === ')') {
        if (san) break;
        this.#at++;
        commitPrelude();
        return undefined;
      }
      // NAGs
      else if (ch === '$') {
        const nag = this.#parseNag();
        if (nag) addNag(nag);
      }
      // Move numbers
      else if (/[1-9]/.test(ch)) {
        if (san) break;

        // just in case
        commitPrelude();
        preludeText = undefined;
        preludeSquares = undefined;
        preludeArrows = undefined;

        const { num, color: _color } = this.#parseFullNumber();
        color = _color;
        this.#lastFullMove = moveFull = num;
      }
      // SANs
      else if (/\w/.test(ch) || this.#isNullMoveStart()) {
        if (san) break;

        const parsed = this.#parseSan();
        san = parsed?.san ?? '';
        if (parsed?.inlineNag) addNag(parsed.inlineNag);
      }
      // Ignore all else
      else {
        this.#at++;
      }
    }

    if (this.#at === this.source.length && !san) {
      // PGN только с комментарием
      commitPrelude();
      if (!preludeText && !preludeSquares && !preludeArrows) {
        if (color || moveFull || comment) {
          console.error('PgnMovetextParser: invalid last move');
        }
      }
      return undefined;
    }

    if (!color) color = 'black';
    if (color === 'black' && !moveFull) {
      moveFull = this.#lastFullMove;
    }

    const result: IPgnMove | undefined =
      san && color && moveFull
        ? {
            id: moveId,
            altId: this.#currentAltId,
            san,
            moveFull,
            color,
            comment,
            timeRemaining,
            timeElapsed,
            coloredSquares,
            coloredArrows,
            score,
            points,
            alts,
            nags,
          }
        : undefined;

    if (!result) {
      console.error('PgnMovetextParser: invalid move', this.#at);
    }

    return result;
  }

  #parseFullNumber(): { num: number; color: 'white' | 'black' } {
    let ch = this.source[this.#at];
    let number = '';

    let dots = 0;

    while (ch && /\d|\./.test(ch)) {
      number += ch;

      if (ch === '.') {
        dots++;
      } else if (dots) {
        // после точек номер заканчивается и может начаться рокировка,
        // записанная нулями, типа 4.0-0-0, поэтому остановим обработку
        break;
      }

      this.#at++;
      ch = this.source[this.#at];
    }

    return {
      num: parseInt(number),
      color: number.endsWith('...') ? ('black' as const) : ('white' as const),
    };
  }

  #parseComment(): { comment?: string; commands?: Record<string, string[]> } | undefined {
    const end = this.source.indexOf('}', this.#at + 1);

    if (end === -1) {
      this.#at = this.source.length;
      return undefined;
    }

    this.#at++;

    let comment = '';
    let commands: Record<string, string[]> | undefined;

    while (this.#at < end) {
      const ch = this.source[this.#at]!;

      if (!comment && ch === ' ') {
        this.#at++;
        continue;
      }

      if (ch === '[' && this.source[this.#at + 1] === '%') {
        const spBefore = this.source[this.#at - 1] === ' ';

        const d = this.#parseCommentCommands();

        if (d) {
          if (!commands) {
            commands = {};
          }
          commands[d[0]] = d[1];
        }

        const spAfter = this.source[this.#at] === ' ';

        if (spBefore && spAfter) this.#at++;
        if (!spBefore && !spAfter) comment += ' ';
      } else {
        comment += ch;
        this.#at++;
      }
    }

    this.#at = end + 1;
    comment = comment.trim();

    if (comment || commands) {
      const result = { comment: comment || undefined, commands };
      return result;
    }
  }

  #parseNag(): number | undefined {
    this.#at++;

    let ch = this.source[this.#at];
    let nagText = '';

    while (ch?.match(/\d/) && this.#at < this.source.length) {
      nagText += ch || '';
      this.#at++;
      ch = this.source[this.#at];
    }

    const nag = parseInt(nagText, 10);

    return nag || undefined;
  }

  #parseCommentCommands(): [string, string[]] | undefined {
    this.#at++;

    const end = this.source.indexOf(']', this.#at);
    let command = this.source.substring(this.#at, end);
    this.#at = end + 1;

    command = command.trim();

    if (command && command.startsWith('%')) {
      const name = command.substring(1, command.indexOf(' '));
      const argsText = name ? command.substring(1 + name.length + 1) : null;
      const args = argsText?.split(/\s*,\s*/);

      if (name && args?.length) {
        return [name, args];
      }
    }
  }

  #isNullMoveStart(): boolean {
    if (this.source[this.#at] !== '-') return false;
    if (this.source[this.#at + 1] !== '-') return false;
    const next = this.source[this.#at + 2];
    return next === undefined || /[\s)({]/.test(next);
  }

  #parseSan(): { san: string; inlineNag?: number } | undefined {
    let ch = this.source[this.#at];
    let san = '';

    while (
      ch &&
      ch !== ' ' &&
      ch !== '\n' &&
      ch !== '\r' &&
      ch !== '\t' &&
      ch !== ')' &&
      ch !== '(' &&
      ch !== '{'
    ) {
      san += ch;
      this.#at++;
      ch = this.source[this.#at];
    }

    // нормализация рокировки (0-0 → O-O, 0-0-0 → O-O-O)
    if (san[0] === '0') {
      san = san.replace(/0/g, 'O');
    }

    // инлайновые аннотации (?,! и т.д.) → NAGs
    const last = san[san.length - 1];
    if (last === '?' || last === '!') {
      let sym = last;

      const preLast = san[san.length - 2];
      if (preLast === '?' || preLast === '!') {
        sym = preLast + sym;
      }

      return { san: san.slice(0, -sym.length), inlineNag: INLINE_NAG_MAP[sym] };
    }

    return { san };
  }
}

/*
    ____,-------------------------------,____
    \   |         Сериализаторы         |   /
    /___|-------------------------------|___\
*/

// Out: PGN movetext with prelude
function serializeVariant(variant: IPgnVariantDto<IMoveDto>, startFullMove?: number): string {
  const parts: string[] = [];
  const prelude = serializePrelude(variant);
  if (prelude) parts.push(prelude);
  if (variant.moves?.length) {
    parts.push(serializeMoveDtos(variant.moves, startFullMove ?? 1));
  }
  return parts.join(' ');
}

// Out: Prelude comment
function serializePrelude(variant: IPgnVariantDto<IMoveDto>): string | undefined {
  const parts: string[] = [];

  const commands: string[] = [];
  if (variant.coloredSquares?.length) {
    commands.push(`[%csl ${variant.coloredSquares.map((s) => `${s.color}${s.square}`)}]`);
  }
  if (variant.coloredArrows?.length) {
    commands.push(`[%cal ${variant.coloredArrows.map((a) => `${a.color}${a.from}${a.to}`)}]`);
  }

  if (variant.prelude) {
    parts.push(`{${escapeComment(variant.prelude)}}`);
  }
  if (commands.length) {
    parts.push(`{${commands.join(' ')}}`);
  }

  return parts.length ? parts.join(' ') : undefined;
}

// Out: PGN movetext
function serializeMoveDtos(moves: IMoveDto[], _initialMove = 1): string {
  const pgn: string[] = [];
  const comments: string[] = [];
  moves.forEach((cur, i) => {
    // half move

    const color = cur.color ?? (_initialMove + (i % 2) === 1 ? 'white' : 'black');
    const moveFull =
      cur.moveFull ?? _initialMove + Math.floor((i + (color === 'white' ? 1 : 0)) / 2);

    if (color === 'white') {
      pgn.push(`${moveFull}. ${cur.san}`);
    } else {
      const prev = moves[i - 1];
      const isBroken = prev?.alts || prev?.comment || i === 0;

      if (isBroken) {
        pgn.push(`${moveFull}...`);
      }

      pgn.push(cur.san);
    }

    // NAGs

    if (cur.nags) {
      pgn.push(cur.nags.map((g) => `$${g}`).join(' '));
    }

    // comments

    if (
      cur.comment ||
      cur.timeElapsed ||
      cur.timeRemaining ||
      cur.coloredSquares ||
      cur.coloredArrows ||
      cur.points !== undefined
    ) {
      if (cur.timeElapsed) {
        comments.push(`[%emt ${secondsToTime(cur.timeElapsed)}]`);
      }
      if (cur.timeRemaining) {
        comments.push(`[%clk ${secondsToTime(cur.timeRemaining)}]`);
      }
      if (cur.coloredSquares) {
        comments.push(`[%csl ${cur.coloredSquares.map((s) => `${s.color}${s.square}`)}]`);
      }
      if (cur.coloredArrows) {
        comments.push(`[%cal ${cur.coloredArrows.map((a) => `${a.color}${a.from}${a.to}`)}]`);
      }
      if (cur.points !== undefined) {
        comments.push(`[%points ${cur.points}]`);
      }
      if (cur.comment) {
        comments.push(escapeComment(cur.comment));
      }

      pgn.push(`{${comments.join(' ')}}`);
      comments.splice(0, comments.length);
    }

    // alternatives

    if (cur.alts) {
      cur.alts.forEach((a) => {
        const body = serializeVariant(a, moveFull);
        pgn.push(`(${body})`);
      });
    }
  });

  return pgn.join(' ');
}

/*
    ____,-------------------------------,____
    \   |             Утилиты           |   /
    /___|-------------------------------|___\
*/

// In: Hello \/ "world", Out: Hello \\/ \"world\"
export function escapeTagValue(v: string): string {
  return v.replaceAll('\\', '\\\\').replaceAll('"', '\\"');
}

// In: Hello \\/ \"world\", Out: Hello \/ "world"
function unescapeTagValue(v: string): string {
  return v.replace(/\\([\\"])/g, '$1');
}

// In: 1:29:53, Out: 5393
export function timeToSeconds(timeStr: string): number | undefined {
  const parts = timeStr.split(':');

  if (parts.length > 3) return undefined;

  let seconds = 0;

  const s = parts.pop();
  if (s) seconds += parseInt(s, 10);

  const m = parts.pop();
  if (m) seconds += parseInt(m, 10) * 60;

  const h = parts.pop();
  if (h) seconds += parseInt(h, 10) * 60 * 60;

  return seconds;
}

// In: 5393, Out: 1:29:53
export function secondsToTime(timeSec: number): string {
  const hours = Math.floor(timeSec / 3600);
  const minutes = Math.floor((hours ? timeSec % 3600 : timeSec) / 60);
  const seconds = timeSec % 60;

  return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export const INLINE_NAG_MAP: Record<string, number> = {
  '!': 1,
  '?': 2,
  '!!': 3,
  '??': 4,
  '!?': 5,
  '?!': 6,
};

// In: 3, Out: "!!"
export function nagToSymbol(nag: number): string | null {
  switch (nag) {
    case 1:
      return '!';
    case 2:
      return '?';
    case 3:
      return '!!';
    case 4:
      return '??';
    case 5:
      return '!?';
    case 6:
      return '?!';
    case 7:
    case 8:
      return '⊡';
    case 10:
    case 11:
    case 12:
      return '=';
    case 13:
      return '∞';
    case 14:
      return '⩲';
    case 15:
      return '⩱';
    case 16:
      return '±';
    case 17:
      return '∓';
    case 18:
    case 20:
      return '+−';
    case 19:
    case 21:
      return '−+';
    case 22:
    case 23:
      return '⨀';
    case 32:
    case 33:
      return '⟳';
    case 36:
    case 37:
      return '↑';
    case 40:
    case 41:
      return '→';
    case 132:
    case 133:
      return '⇆';
    case 138:
      return '⨁';
    case 140:
      return '∆';
    case 141:
      return '∇';
    case 142:
      return '⌓';
    case 145:
      return 'RR';
    case 146:
      return 'N';
    case 239:
      return '⇔';
    case 240:
      return '⇗';
    case 242:
      return '⟫';
    case 243:
      return '⟪';
    case 244:
      return '✕';
    case 245:
      return '⊥';
    case 256:
      return '=/∞';
    default:
      return null;
  }
}

// In: ["Rd4", "Gd5"], Out: [{color: "R", square: "d4"}, {color: "G", square: "d5"}]
function mapClsSquares(values: string[]): IColoredSquare[] {
  return values.map((s) => {
    const color = s[0] as IColoredSquare['color'];
    const square = s.substring(1) as IColoredSquare['square'];
    return { color, square };
  });
}

// In: ["Ra8d8", "Ge8c8"], Out: [{color: "R", from: "a8", to: "d8"}, {color: "G", from: "e8", to: "c8"}]
function mapCalSquares(values: string[]): IColoredArrow[] {
  return values.map((a) => {
    const color = a[0] as IColoredArrow['color'];
    const from = a.substring(1, 3) as IColoredArrow['from'];
    const to = a.substring(3) as IColoredArrow['to'];
    return { color, from, to };
  });
}

function mapEval(values: string[]): IEvaluationScore | undefined {
  const [rawScore, rawDepth] = values;
  const mateRx = /[#M]/g;

  if (!rawScore) return undefined;

  const type = rawScore.match(mateRx) ? 'mate' : 'cp';
  const value = type === 'mate' ? parseInt(rawScore.replace(mateRx, ''), 10) : parseFloat(rawScore);
  const depth = rawDepth ? parseInt(rawDepth, 10) : undefined;

  return { type, value, depth };
}

function escapeComment(c: string) {
  return c.replaceAll('}', '');
}

function mapInteger(num: string): number | undefined {
  const val = parseInt(num, 10);
  return isNaN(val) ? undefined : val;
}

// ------------------------------------------
// Тег Tokens: группы «тип:клетки» через пробел, клетки слиты в одну строку.
// In: "star:b3d4 wall:e5", Out: [{type: 'star', square: 'b3'}, ...]

const TOKEN_GROUP_RE = /^([a-z][a-z0-9-]*):((?:[a-h][1-8])+)$/;

export function parseTokens(value?: string): IToken[] | undefined {
  if (!value) return undefined;

  // Клетка названа в нескольких группах — побеждает последняя
  const bySquare = new Map<SquareName, IToken>();

  for (const group of value.trim().split(/\s+/)) {
    // Группа с посторонним символом отбрасывается целиком: разбор по два
    // символа сдвинул бы все клетки после ошибки и дал правдоподобный мусор
    const match = TOKEN_GROUP_RE.exec(group);
    if (!match) continue;

    const type = match[1]!;
    for (const square of match[2]!.match(/../g) as SquareName[]) {
      bySquare.set(square, { type, square });
    }
  }

  return bySquare.size ? [...bySquare.values()] : undefined;
}

export function serializeTokens(tokens?: IToken[]): string | undefined {
  if (!tokens?.length) return undefined;

  // Типы и клетки по алфавиту, чтобы load → save давал ту же строку
  const groups = new Map<string, Set<SquareName>>();
  for (const t of tokens) {
    if (!groups.has(t.type)) groups.set(t.type, new Set());
    groups.get(t.type)!.add(t.square);
  }

  return [...groups.keys()]
    .sort()
    .map((type) => `${type}:${[...groups.get(type)!].sort().join('')}`)
    .join(' ');
}

// ------------------------------------------
// Тег TestScoreLadder: «<максимум звезд> <ступень>:<порог> ...».
// In: "3 2:5 1:8" — до 5 ходов 3 звезды, до 8 — 2, дальше 1

const SCORE_LADDER_PAIR_RE = /^([12]):([1-9]\d{0,2})$/;

export function parseScoreLadder(value?: string): IScoreLadder | undefined {
  if (!value) return undefined;

  const [head, ...pairs] = value.trim().split(/\s+/);
  if (!head || !/^[123]$/.test(head)) return undefined;

  const maxStars = parseInt(head, 10);
  const thresholds: IScoreLadder['thresholds'] = {};

  for (const pair of pairs) {
    // Битая пара отбрасывается целиком, как группы в теге Tokens
    const m = SCORE_LADDER_PAIR_RE.exec(pair);
    if (!m) continue;

    const stars = parseInt(m[1]!, 10);
    if (stars >= maxStars) continue;
    thresholds[stars] = parseInt(m[2]!, 10);
  }

  return { maxStars, thresholds };
}

export function serializeScoreLadder(ladder?: IScoreLadder): string | undefined {
  // Максимум одна звезда — значение по умолчанию, тег не пишем
  if (!ladder || ladder.maxStars < 2) return undefined;

  const parts = [String(Math.min(ladder.maxStars, 3))];
  // Ступени по убыванию — round-trip дает ту же строку
  for (const stars of [2, 1]) {
    const moves = ladder.thresholds[stars];
    if (stars < ladder.maxStars && moves && moves > 0) parts.push(`${stars}:${moves}`);
  }

  return parts.join(' ');
}
